import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import {
  getQuiztopiyaCurriculumsForAdmin,
  getQuiztopiyaQuizzesForAdmin,
  getQuiztopiyaSubTopicsForAdmin,
  getQuiztopiyaTopicsForAdmin,
} from './adminApi';
import { RootState } from '../..';
import { IQuiz } from '../../QuizSlice';

export interface IQuiztopiyaSubTopicsForAdmin {
  _id: string;
  name: string;
}

export interface IQuiztopiyaTopicsForAdmin {
  _id: string;
  name: string;
}

export interface IQuiztopiyaSubjectLevelForAdmin {
  name: string;
}

export interface IQuiztopiyaSubjectForAdmin {
  code: string;
  name: string;
  subjectLevels: Array<IQuiztopiyaSubjectLevelForAdmin>;
}

export interface IQuiztopiyaGradeForAdmin {
  name: string;
  subjects: Array<IQuiztopiyaSubjectForAdmin>;
}

export interface IQuiztopiyaCurriculumForAdmin {
  _id: string;
  name: string;
  grades: Array<IQuiztopiyaGradeForAdmin>;
}

export interface IInitialQuiztopiyaState {
  status: 'idle' | 'loading' | 'rejected';
  quizzesStatus: 'idle' | 'loading' | 'rejected';
  adminCurriculums: IQuiztopiyaCurriculumForAdmin[];
  adminTopics: IQuiztopiyaTopicsForAdmin[];
  adminSubTopics: IQuiztopiyaSubTopicsForAdmin[];
  adminQuizzes: IQuiz[];
}

const initialState: IInitialQuiztopiyaState = {
  status: 'idle',
  quizzesStatus: 'idle',
  // Admin
  adminCurriculums: [],
  adminTopics: [],
  adminSubTopics: [],
  adminQuizzes: [],
};

export const QuiztopiyaSlice = createSlice({
  name: 'quiztopiya',
  initialState,
  reducers: {
    resetAdminCurriculums: (state) => {
      state.adminCurriculums = [];
    },
    resetAdminTopics: (state) => {
      state.adminTopics = [];
    },
    resetAdminSubTopics: (state) => {
      state.adminSubTopics = [];
    },
    resetAdminQuizzes: (state) => {
      state.adminQuizzes = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getQuiztopiyaCurriculumsForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminCurriculums = action.payload || [];
      })
      .addCase(getQuiztopiyaTopicsForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminTopics = action.payload || [];
      })
      .addCase(getQuiztopiyaSubTopicsForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminSubTopics = action.payload || [];
      })
      .addCase(getQuiztopiyaQuizzesForAdmin.pending, (state) => {
        state.quizzesStatus = 'loading';
      })
      .addCase(getQuiztopiyaQuizzesForAdmin.fulfilled, (state, action) => {
        state.quizzesStatus = 'idle';
        state.adminQuizzes = action.payload || [];
      })
      .addCase(getQuiztopiyaQuizzesForAdmin.rejected, (state) => {
        state.quizzesStatus = 'rejected';
      });

    // Matchers
    builder
      .addMatcher(
        isPending(
          getQuiztopiyaCurriculumsForAdmin,
          getQuiztopiyaTopicsForAdmin,
          getQuiztopiyaSubTopicsForAdmin
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getQuiztopiyaCurriculumsForAdmin,
          getQuiztopiyaTopicsForAdmin,
          getQuiztopiyaSubTopicsForAdmin
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  resetAdminCurriculums,
  resetAdminTopics,
  resetAdminSubTopics,
  resetAdminQuizzes,
} = QuiztopiyaSlice.actions;

export const getQuiztopiyaState = (state: RootState): RootState['quiztopiya'] =>
  state.quiztopiya;

export * from './adminApi';
export default QuiztopiyaSlice.reducer;
